import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can as rbacCan, ROLE_LABEL } from "@/lib/rbac";
import useIdleLogout from "@/hooks/useIdleLogout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  GaugeIcon, CylinderIcon, WalletIcon, ReceiptIcon, UsersThreeIcon,
  SignOutIcon, FlameIcon, TruckIcon, ChartLineUpIcon, BankIcon,
  UserIcon, CheckSquareIcon, MagnifyingGlassIcon, TagIcon, ShieldCheckIcon,
  BuildingsIcon, UploadSimpleIcon, WarningOctagonIcon, ListIcon, XIcon, FileTextIcon,
} from "@phosphor-icons/react";
import FontSizeControl from "@/components/app/FontSizeControl";

const NAV = [
  { to: "/", label: "Dashboard", icon: GaugeIcon, end: true, testid: "nav-dashboard", hideForRoles: ["delivery_entry"] },
  { to: "/cylinders", label: "Cylinder Stock", icon: CylinderIcon, testid: "nav-cylinders", needs: "cylinders:read", hideForRoles: ["delivery_entry"] },
  { to: "/delivery-boys", label: "Delivery Boys", icon: TruckIcon, testid: "nav-delivery-boys", needs: "boys:read", hideForRoles: ["delivery_entry"] },
  { to: "/customers", label: "Commercial Customers", icon: UserIcon, testid: "nav-customers", needs: "customers:read", hideForRoles: ["delivery_entry"] },
  { to: "/deliveries", label: "Deliveries", icon: ReceiptIcon, testid: "nav-deliveries", needs: "customers:read" },
  { to: "/collections", label: "Collections", icon: WalletIcon, testid: "nav-collections", needs: "collections:read" },
  { to: "/expenses", label: "Expenses", icon: ReceiptIcon, testid: "nav-expenses", needs: "expenses:read" },
  { to: "/daily-closing", label: "Daily Closing", icon: CheckSquareIcon, testid: "nav-closing", needs: "closing:read" },
  { to: "/reports", label: "Reports", icon: ChartLineUpIcon, testid: "nav-reports", needs: "reports:read" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isOwner = user?.role === "owner";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleLogout = useCallback(async () => {
    await logout();
    nav("/login", { replace: true });
  }, [logout, nav]);

  // Auto-logout after 15 minutes of inactivity
  useIdleLogout(handleLogout, 15 * 60 * 1000);

  // Close the mobile drawer on nav change
  const closeMobile = () => setMobileNavOpen(false);

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[240px_1fr] bg-[#F9FAFB]">
      {/* Mobile drawer backdrop */}
      {mobileNavOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          onClick={closeMobile}
          data-testid="mobile-nav-backdrop"
        />
      )}

      {/* Sidebar — always visible on lg+, slide-in on mobile */}
      <aside
        className={`bg-white border-r border-slate-200 flex flex-col z-40
                    lg:static lg:translate-x-0
                    fixed inset-y-0 left-0 w-64 transform transition-transform duration-200 ease-out
                    ${mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="app-sidebar"
      >
        <div className="h-16 px-6 flex items-center justify-between gap-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_gas-stock-accounts/artifacts/2fd21ssu_b2f93328dedc3d7c86967e492aa03b39-removebg-preview.png"
                alt="Indane"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="font-display font-black text-brand-blue leading-none">SAI SAYAJI</div>
              <div className="overline text-brand-orange-ink mt-1" style={{ fontSize: 12 }}>Indane · Stock · Accounts</div>
            </div>
          </div>
          <button
            onClick={closeMobile}
            className="lg:hidden text-slate-500 hover:text-brand-blue"
            data-testid="mobile-nav-close"
          >
            <XIcon size={20} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV
            .filter((n) => !n.hideForRoles || !n.hideForRoles.includes(user?.role))
            .filter((n) => !n.needs || rbacCan(user, n.needs))
            .map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-brand-orange bg-brand-orange-tint text-brand-blue font-semibold"
                    : "border-transparent text-slate-600 hover:text-brand-blue hover:bg-slate-50"
                }`
              }
            >
              <n.icon size={18} weight="regular" />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {rbacCan(user, "audit:read") && (
            <NavLink
              to="/audit-log"
              data-testid="nav-audit"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-brand-orange bg-brand-orange-tint text-brand-blue font-semibold"
                    : "border-transparent text-slate-600 hover:text-brand-blue hover:bg-slate-50"
                }`
              }
            >
              <ShieldCheckIcon size={18} />
              <span>Audit Log</span>
            </NavLink>
          )}
          {isOwner && (
            <>
              <NavLink
                to="/invoices"
                data-testid="nav-invoices"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-brand-orange bg-brand-orange-tint text-brand-blue font-semibold"
                      : "border-transparent text-slate-600 hover:text-brand-blue hover:bg-slate-50"
                  }`
                }
              >
                <FileTextIcon size={18} />
                <span>Invoices</span>
              </NavLink>
              <NavLink
                to="/settings/branches"
                data-testid="nav-branches"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-brand-orange bg-brand-orange-tint text-brand-blue font-semibold"
                      : "border-transparent text-slate-600 hover:text-brand-blue hover:bg-slate-50"
                  }`
                }
              >
                <BuildingsIcon size={18} />
                <span>Branches</span>
              </NavLink>
              <NavLink
                to="/settings/expense-categories"
                data-testid="nav-expense-categories"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-brand-orange bg-brand-orange-tint text-brand-blue font-semibold"
                      : "border-transparent text-slate-600 hover:text-brand-blue hover:bg-slate-50"
                  }`
                }
              >
                <TagIcon size={18} />
                <span>Categories</span>
              </NavLink>
              <NavLink
                to="/settings/reset"
                data-testid="nav-data-reset"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-[#FF2400] bg-red-50 text-[#FF2400] font-semibold"
                      : "border-transparent text-slate-600 hover:text-[#FF2400] hover:bg-red-50"
                  }`
                }
              >
                <WarningOctagonIcon size={18} />
                <span>Data Reset</span>
              </NavLink>
              <NavLink
                to="/settings/business"
                data-testid="nav-business"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-brand-orange bg-brand-orange-tint text-brand-blue font-semibold"
                      : "border-transparent text-slate-600 hover:text-brand-blue hover:bg-slate-50"
                  }`
                }
              >
                <BuildingsIcon size={18} />
                <span>Business Profile</span>
              </NavLink>
              <NavLink
                to="/staff"
                data-testid="nav-staff"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-brand-orange bg-brand-orange-tint text-brand-blue font-semibold"
                      : "border-transparent text-slate-600 hover:text-brand-blue hover:bg-slate-50"
                  }`
                }
              >
                <UsersThreeIcon size={18} />
                <span>Staff</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-slate-200 p-4">
          <div className="text-xs text-slate-500">Signed in as</div>
          <div className="text-sm font-semibold text-brand-blue truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="overline text-brand-orange-ink mt-1" data-testid="current-user-role">{ROLE_LABEL[user?.role] || user?.role}</div>
          {user?.branch_name && (
            <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 border border-brand-blue text-brand-blue" data-testid="current-user-branch">
              <BuildingsIcon size={11} />
              <span className="font-mono-num text-xs">{user.branch_name}</span>
            </div>
          )}
          <Button
            variant="ghost"
            className="mt-3 w-full justify-start px-2 rounded-none text-slate-700 hover:text-[#FF2400] hover:bg-transparent"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <SignOutIcon size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 bg-white/70 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="p-2 -ml-2 text-brand-blue hover:bg-slate-100"
              data-testid="mobile-nav-open"
              aria-label="Open menu"
            >
              <ListIcon size={22} />
            </button>
            <div className="w-8 h-8 bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
              <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_gas-stock-accounts/artifacts/2fd21ssu_b2f93328dedc3d7c86967e492aa03b39-removebg-preview.png"
                alt="Indane" className="w-full h-full object-contain" />
            </div>
            <span className="font-display font-black text-brand-blue text-sm">SAYAJI INDANE</span>
          </div>
          <div className="hidden lg:block overline text-slate-500">Distributor Console</div>
          <GlobalSearch />
          <div className="flex items-center gap-2 sm:gap-3">
            <FontSizeControl />
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-slate-900 truncate max-w-[160px]">{user?.email}</div>
              <div className="overline text-slate-500">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-none border-slate-300 hover:border-slate-900 lg:hidden"
              onClick={handleLogout}
              data-testid="logout-button-mobile"
            >
              <SignOutIcon size={14} />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(r.data.results || []);
        setOpen(true);
      } catch (e) {
        console.warn("Global search request failed", e);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    nav(r.link);
    setOpen(false); setQ("");
  };

  return (
    <div ref={ref} className="hidden md:block relative flex-1 max-w-md mx-6" data-testid="global-search">
      <div className="relative">
        <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.length >= 2 && setOpen(true)}
          placeholder="Search customer / invoice / boy / expense…"
          className="rounded-none pl-9 h-9 border-slate-300"
          data-testid="global-search-input"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 shadow-lg z-50 max-h-[400px] overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={`${r.kind}-${r.id}-${i}`}
              onClick={() => go(r)}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
              data-testid={`search-result-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{r.label}</span>
                <span className="overline text-xs" style={{ fontSize: 10 }}>{r.kind}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{r.sub}</div>
            </button>
          ))}
        </div>
      )}
      {open && q.length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 shadow px-3 py-3 text-sm text-slate-500 z-50">No matches for "{q}"</div>
      )}
    </div>
  );
}
